import { missionChangeEntries } from "../utils/missionChange"
import React, { useState, useEffect, useMemo, useRef } from "react"
import cx from "classnames"
import { motion, AnimatePresence } from "framer-motion"
import ActionItemTile from "../comp/ActionItemTile"
import ActionTile from "../comp/ActionTile"
import InviteTile from "../comp/InviteTile"
import BarLoader from "react-spinners/BarLoader"
import SimpleBarReact from "simplebar-react"
import Card from "../comp/Card"
import DemoTourWelcome from "../comp/DemoTourWelcome"
import { endDemoTour, getDemoTourStatus } from "../services/demoTour/demoTour.api"
import { isDemoTourOn, isDemoTourTester } from "../services/demoTour/demoTourFlag"
import ErrorBoundary from "../ErrorBoundary"
import Tip from "../comp/Tip"
import Bubble from "../comp/Bubble"
import { getLeavesBadge } from "./Board"
import uniqBy from "lodash/uniqBy"
import uniq from "lodash/uniq"
import { getActionItems } from "../services/api/v2/actionItem"
import { onActionItemChanges, onMissionDeleted } from "../services/actionItem/actionItemFeed"

import { utcToIsoString } from "../utils/dates"

import { paginateAll, paginateChunked } from "../services/api/utils"

import { getMyMissions } from "../services/api/v2/mission"
import { getMyVendorInvoiceApprovals } from "../services/api/v2/vendorInvoice"
import { countWaitingSignOffs } from "../comp/signOff/SignOffInbox"
import { isSignOffNotification } from "../utils/signOffs"
import notificationStore, { useNotifications } from "../services/notification/notificationStore"
import apiMission from "../services/mission/mission.api"

import {
    PiTriangleDuotone,
    PiHexagonDuotone,
    PiNavigationArrowDuotone,
    PiIceCreamDuotone,
    PiCircleDuotone,
    PiImageBrokenDuotone,
} from "react-icons/pi"
import { GiCosmicEgg } from "react-icons/gi"
import { toast } from "react-toastify"
import history, { addParam, removeParam } from "../comp/History"
import { publishScreenTabs } from "../utils/screenTabs"
import { darkThemes, useDebouncedEffect, getObjectId, getRefId, makeHash, isGtGr } from "../utils/utils"

import { AiTwotoneThunderbolt } from "react-icons/ai"
import TaskMaster from "../comp/TaskMaster"

import Loadable from "../comp/Loadable"
import { isUserEmailVerified } from "../comp/PeopleUtils"
import { canAccessOrgSpace } from "../comp/OrgUtils"
import { recommender } from "../ai/recommender"
import "./Home.scss"

import { subWeeks, addWeeks, startOfWeek, endOfWeek, subSeconds } from "date-fns"

const SnowFall = Loadable({
    loader: () => import("../comp/SnowFall"),
    loading: () => <span />,
})

const AcceptAllInvites = Loadable({
    loader: () => import("../comp/AcceptAllInvites"),
    loading: () => <span />,
})

const SignOffInbox = Loadable({
    loader: () => import("../comp/signOff/SignOffInbox"),
    loading: () => null,
})

const VendorInvoiceApprovals = Loadable({
    loader: () => import("../comp/VendorInvoiceApprovals"),
    loading: () => <span />,
})

const TeamActivity = Loadable({
    loader: () => import("../comp/TeamActivity"),
    loading: () => <span />,
})

const TaskStatus = Loadable({
    loader: () => import("../comp/TaskStatus"),
    loading: () => null,
})
const WelcomeToMission = Loadable({
    loader: () => import("../module/WelcomeToMission"),
    loading: () => <span />,
})
const WelcomeToOrg = Loadable({
    loader: () => import("../module/WelcomeToOrg"),
    loading: () => <span />,
})

const fields = [
    "title",
    "startDate",
    "estimatedDuration",
    "endDate",
    "org",
    "missionId",
    "action",
    "planItem",
    "status",
    "col",
    "week",
    "startHour",
    "people",
    "+seen",
    "+checklist",
    "percentComplete",
    "metaType",
    "approvals",
    "signOffs",
]

const CHECK_TILE_IDS = ["helper-leaves", "helper-sign-offs", "helper-vendor-invoices"]

const FULL_RELOAD_MS = 5 * 60 * 1000

// More GT SSO invites than this show as one "Invited to N missions" card
const GT_SSO_INVITES_BUNDLE_AT = 50

/** A GT mission invite to a gr.gt.com email: the person is an active member already */
const isGtSsoInvite = (notice) =>
    isGtGr({ _id: getObjectId(notice?.org) }) && /@gr\.gt\.com$/i.test(notice?.ref?.inviteeEmail || "")

/**
 * GT SSO invites in hand plus the server's total (only a page of invites is loaded).
 * Bundled when there are more than GT_SSO_INVITES_BUNDLE_AT.
 */
const gtSsoInviteCount = (notificationList, serverTotal) => {
    const loaded = (notificationList || []).filter(
        (n) => n.isDisabled !== true && n.type === "invite" && isGtSsoInvite(n),
    ).length

    // The total is from before any clear, so it never counts up past what's loaded once gone
    const count = loaded ? Math.max(loaded, serverTotal || 0) : 0

    return { loaded, count, bundled: count > GT_SSO_INVITES_BUNDLE_AT }
}

/** Live mission invites to list one by one: bundled GT SSO ones are left out */
const listableMissionInvites = (notificationList, serverTotal) => {
    const invites = (notificationList || []).filter((n) => n.isDisabled !== true && n.type === "invite")

    return gtSsoInviteCount(notificationList, serverTotal).bundled ? invites.filter((n) => !isGtSsoInvite(n)) : invites
}

// What a task tile reads off its mission
const TILE_MISSION_FIELDS = [
    "_id",
    "title",
    "projectCode",
    "theme",
    "org",
    "projectStatus",
    "projectType",
    "isProcess",
    "kickedOff",
    "client",
    "importance",
    "planStartDate",
    "planEndDate",
    "missionStop",
    "isProposal",
    "noTimesheets",
]

const TASK_FIELDS = [...fields.map((f) => f.replace(/^\+/, "")), "_id", "_archived"]

const pickTaskFields = (doc) => {
    const picked = {}
    TASK_FIELDS.forEach((f) => {
        if (doc[f] !== undefined) picked[f] = doc[f]
    })
    return picked
}

const Home = ({ app, missionChange, taskMasterRef }) => {
    const notificationList = useNotifications((s) => s.list)
    const updatedAtRef = useRef()
    // Week the cached task windows were built for; a new week means a full reload
    const windowWeekRef = useRef()
    const lastFullLoadRef = useRef(0)
    // Bumped by every full load so an older, slower one cannot overwrite it
    const loadRunRef = useRef(0)
    // Task id -> when the socket took it off the list (done, archived, unassigned, deleted)
    const evictedAtRef = useRef(new Map())
    const getTasksRanRef = useRef()
    const lastContextRef = useRef(app.state.person?.appUiState?.homeState?.tab || "workAI")
    const actionItemsRef = useRef([])

    const missionMapRef = useRef({})

    const missionCountRef = useRef()

    useEffect(() => {
        const go = async () => {
            const twoWeeksAgoStart = utcToIsoString(startOfWeek(subWeeks(new Date(), 2), { weekStartsOn: 1 }))
            const oneWeekAheadEnd = utcToIsoString(endOfWeek(addWeeks(new Date(), 1), { weekStartsOn: 1 }))
            const myMissionCount = await getMyMissions({
                limit: 1,
                startDate: `lte ${oneWeekAheadEnd}`,
                endDate: `gte ${twoWeeksAgoStart}`,
                count: true,
            })

            missionCountRef.current = myMissionCount.count
        }

        go()
    }, [])

    function getGreeting() {
        const now = new Date()
        const hour = now.getHours()

        let type
        if (hour < 12) type = "morning"
        else if (hour < 18) type = "afternoon"
        else type = "evening"

        return <p className="magda-clean">{`Good ${type} ${app.state.person?.firstName}`}</p>
    }

    const q = new URLSearchParams(window.location.search)

    const [myTheme, setMyTheme] = useState(
        typeof app.state.person.theme === "object" ||
            app.state.person.theme === null ||
            app.state.person.theme === "null" ||
            !app.state.person.theme
            ? "empty"
            : app.state.person.theme || localStorage.getItem("dna-x-theme") || "empty",
    )

    const [context, setContext] = useState(lastContextRef.current || "workAI")
    const [loading, setLoading] = useState(context === "workAI")
    const [recommendations, setRecommendations] = useState([])
    const recommendationsRef = useRef([])
    const [showMissionInvite, setShowMissionInvite] = useState()
    const [showAllMissinInvites, setShowAllMissinInvites] = useState()
    const [showInvoiceApprovals, setShowInvoiceApprovals] = useState(false)
    const [showSignOffs, setShowSignOffs] = useState(false)
    const [showOrgInvite, setShowOrgInvite] = useState()
    // The demo tour (services/demoTour): {available, eligible, tour} for somebody in no org, and its welcome
    const [demoTourStatus, setDemoTourStatus] = useState(null)
    const [showDemoTour, setShowDemoTour] = useState(false)

    const inviteNoticeId = q.get("invite-notice")

    const setTheme = (theme) => {
        setMyTheme(theme || "empty")

        app.personUpdate({
            theme: theme || "empty",
        })
        localStorage.setItem("dna-x-theme", theme)
        if (!app.hasSeenMsg("x-home-tour-eggs")) app.setUiSeenFlags("x-home-tour-eggs")
    }

    useDebouncedEffect(
        () => {
            if (!app.state.person._id || !app.state.orgs.length) {
                return
            }

            leaveChecks()

            // No org in the workspace has vendors, expenses or equipment on - nothing can
            // be awaiting this person's approval, so skip the call
            if (app.state.orgs.some((o) => o.hasPartners || !o.expensesDisabled || o.hasEquipment === true)) {
                invoiceApprovalChecks()
            }
        },
        [app.state.person?._id, app.state.orgs, notificationList],
        100,
    )

    // Only a sign-off notification can change the "waiting for you" count, so the
    // count query per organisation does not re-run on every other notification or
    // on a mark-read.
    const signOffNotificationKey = useMemo(
        () =>
            (notificationList || [])
                .filter((n) => isSignOffNotification(n.type))
                .map((n) => n._id)
                .join(","),
        [notificationList],
    )

    useDebouncedEffect(
        () => {
            if (!app.state.person._id || !app.state.orgs.length) {
                return
            }

            signOffChecks()
        },
        [app.state.person?._id, app.state.orgs, signOffNotificationKey],
        100,
    )

    useEffect(() => {
        recommendationsRef.current = recommendations || []
    }, [recommendations, app.state.person?._id])

    useDebouncedEffect(
        () => {
            if (inviteNoticeId) {
                const myNotice = notificationStore.getState().list.find((n) => n._id === inviteNoticeId)

                if (myNotice?.type === "orgInvite") {
                    setShowOrgInvite({ data: myNotice })
                } else {
                    const foundRecommendation =
                        recommendations.find((r) => r.data?._id === inviteNoticeId) ||
                        recommendations.find((r) => r.isAllInOne)

                    if (foundRecommendation?.isAllInOne) {
                        setShowAllMissinInvites(foundRecommendation)
                    } else if (foundRecommendation?.isMissionInvite) {
                        setShowMissionInvite(foundRecommendation)
                    }
                }

                removeParam("invite-notice")
            }
        },
        [inviteNoticeId, recommendations.length, app.state.person?._id],
        200,
    )

    useEffect(() => {
        app.setState({
            theme: myTheme,
        })
    }, [myTheme])

    // Another screen can pick the view (?home-tab=taskMaster, mAX's navigate):
    // read on arrival and on every navigation, then removed so it never
    // reapplies. Not a remount (the Navigator's avKey), which would take mAX's
    // panel down with it.
    useEffect(() => {
        const applyRequestedTab = () => {
            const requested = new URLSearchParams(window.location.search).get("home-tab")

            if (!requested) return

            removeParam("home-tab", true)

            if (HOME_TABS.includes(requested) && (requested !== "taskStatus" || !isGtUser(app))) {
                setContext(requested)
                saveHomeTab(app, requested)
            }
        }

        applyRequestedTab()

        return history.listen(applyRequestedTab)
    }, [])

    // What mAX's navigate tool and context read (utils/screenTabs.js).
    useEffect(() => publishScreenTabs("home", { ids: HOME_TABS, active: context }), [context])

    // Incremental by default (only what changed since the last load). A full load
    // replaces the cache, which is the only way to lose tasks that moved out of the
    // windows, were unassigned or archived without this tab seeing the socket event.
    const getTasks = async (forceFull) => {
        const weekStart = startOfWeek(new Date(), { weekStartsOn: 1 })
        const weekStartIso = utcToIsoString(weekStart)

        const full =
            forceFull ||
            !updatedAtRef.current ||
            windowWeekRef.current !== weekStartIso ||
            Date.now() - lastFullLoadRef.current > FULL_RELOAD_MS

        const run = full ? ++loadRunRef.current : loadRunRef.current
        const since = full ? undefined : updatedAtRef.current
        const startedAt = subSeconds(new Date(), 15).toISOString()
        const requestedAt = Date.now()

        const weekStartNextIso = utcToIsoString(addWeeks(weekStart, 1))
        const twoWeeksAgoStart = utcToIsoString(startOfWeek(subWeeks(new Date(), 2), { weekStartsOn: 1 }))
        const oneWeekAheadEnd = utcToIsoString(endOfWeek(addWeeks(new Date(), 1), { weekStartsOn: 1 }))

        const [gottenAgileActionItems, gottenAgileActionItemsNext, gottenMxGActionItems] = await Promise.all([
            paginateAll(
                getActionItems({
                    person: app.state.person._id,
                    select: fields,
                    week: `eq ${weekStartIso}`,
                    updatedAt: since ? `gte ${since}` : undefined,
                    status: ["open", "blocked"],
                }),
            ),
            paginateAll(
                getActionItems({
                    person: app.state.person._id,
                    select: fields,
                    week: `eq ${weekStartNextIso}`,
                    updatedAt: since ? `gte ${since}` : undefined,
                    status: ["open", "blocked"],
                }),
            ),
            paginateAll(
                getActionItems({
                    person: app.state.person._id,
                    select: fields,
                    startDate: `lte ${oneWeekAheadEnd}`,
                    endDate: `gte ${twoWeeksAgoStart}`,
                    updatedAt: since ? `gte ${since}` : undefined,
                    status: ["open", "blocked"],
                }),
            ),
        ])

        // A full load started meanwhile; its result wins
        if (run !== loadRunRef.current) return

        updatedAtRef.current = startedAt

        if (full) {
            windowWeekRef.current = weekStartIso
            lastFullLoadRef.current = Date.now()
        }

        const newAis = uniqBy(
            [
                ...gottenMxGActionItems,
                ...gottenAgileActionItems,
                ...gottenAgileActionItemsNext,
                ...(full ? [] : actionItemsRef.current),
            ],
            "_id",
        ).filter((ai) => ai.status !== "done" && !ai._archived)

        // A full load refetches the cached missions too, so renames and theme changes
        // made elsewhere (other tabs, other people) reach the tiles
        const missionIds = uniq(newAis.map((ai) => ai.missionId)).filter((id) => full || !missionMapRef.current[id])

        const gottenMissions = await paginateChunked({
            fn: (params) => paginateAll(getMyMissions(params)),
            params: {
                id: missionIds,
                participating: true,
                limit: 100,
                select: TILE_MISSION_FIELDS,
            },
            chunkKey: "id",
            chunkSize: 80,
        })

        if (run !== loadRunRef.current) return

        const allMissions = uniqBy([...gottenMissions, ...Object.values(missionMapRef.current)], "_id")
        const allHash = makeHash(allMissions)

        missionMapRef.current = allHash

        // The socket removed these while the request was out; its copy is the stale one
        actionItemsRef.current = newAis.filter((ai) => !(evictedAtRef.current.get(ai._id) >= requestedAt))

        const now = Date.now()
        evictedAtRef.current.forEach((at, id) => {
            if (now - at > 2 * 60 * 1000) evictedAtRef.current.delete(id)
        })

        // Reveal only once the tiles are computed: an empty list before workManager
        // ran reads as "nothing to do", which is not yet known. Same before the orgs are in:
        // the org tiles need them, and their arrival re-runs this load.
        setTimeout(() => {
            workManagerRef.current(actionItemsRef.current, missionMapRef.current)
            if (app.state.orgsLoaded) setLoading(false)
        }, 100)
    }

    // Read from timers and the socket listener, which outlive the render that set them up
    const getTasksRef = useRef()
    getTasksRef.current = getTasks

    // The API holds back tasks on projects I haven't accepted yet. Accepting changes no
    // task, so an incremental load would miss them: load in full.
    const reloadTasksAfterAccept = () => {
        if (getTasksRanRef.current) getTasksRef.current(true)
    }

    // Only a page of invites is loaded: a full page of GT SSO ones asks the server for the total
    const [gtSsoTotal, setGtSsoTotal] = useState(0)
    const gtSsoLoaded = gtSsoInviteCount(notificationList).loaded
    const gtSsoNeedsTotal = gtSsoLoaded >= GT_SSO_INVITES_BUNDLE_AT

    useEffect(() => {
        if (!gtSsoNeedsTotal) return setGtSsoTotal(0)

        apiMission
            .countGtSsoInvites()
            .then(setGtSsoTotal)
            .catch(() => {})
    }, [gtSsoNeedsTotal])

    useDebouncedEffect(() => {
        workManager(actionItemsRef.current, missionMapRef.current)
    }, [notificationList, app.state.orgs, app.state.person?.emailVerified, gtSsoTotal, demoTourStatus])

    // Asked only for somebody in no org (or a tester): the tour is for new sign ups (the API decides who)
    const mayTour = (app.state.orgsLoaded && app.state.orgs.length === 0) || isDemoTourTester(app.state.person)

    useEffect(() => {
        if (!mayTour || isDemoTourOn()) return undefined

        let cancelled = false

        getDemoTourStatus()
            .then((status) => !cancelled && setDemoTourStatus(status || null))
            .catch(() => {})

        return () => {
            cancelled = true
        }
    }, [mayTour])

    // This tab's own edits to a listed task, when its mission is loaded. Everything
    // else (other tabs, other people, missions not in app.state) comes off the socket.
    useDebouncedEffect(
        () => {
            if (!app.state.home || !getTasksRanRef.current) return

            const listed = missionChangeEntries(missionChange).filter(
                (e) => e.for === "actionItem" && actionItemsRef.current.some((ai) => ai._id === e.obj?._id),
            )

            if (!listed.length) return

            // Evict here: the incremental load that follows asks only for open tasks
            // of mine, so a deleted, done or unassigned one never comes back to
            // replace the cached copy.
            listed.forEach((e) => {
                const change = e.change
                const gone =
                    change === "delete" ||
                    change?._archived ||
                    change?.status === "done" ||
                    (Array.isArray(change?.people) && !change.people.some((p) => getRefId(p) === app.state.person?._id))

                if (gone) {
                    evictTask(e.obj._id)
                }
            })

            getTasks()
        },
        [missionChange?.timestamp],
        100,
    )

    useDebouncedEffect(
        () => {
            if (app.state.home) {
                app.showAppAdminTools()
                if (context === "workAI" && lastContextRef.current !== "workAI") {
                    // Back from Task master: the tiles are rebuilt from scratch so they
                    // animate in again. Show the loader meanwhile, not the "not much going
                    // on" island the empty list would draw until the tasks are back.
                    setRecommendations([])
                    setLoading(true)
                    setTimeout(() => {
                        getTasks()
                    }, 300)
                } else {
                    getTasks()
                }
                getTasksRanRef.current = true
                lastContextRef.current = context
            }
        },
        [app.state.home, context, app.state.orgs],
        100,
    )

    const evictTask = (id) => {
        actionItemsRef.current = actionItemsRef.current.filter((ai) => ai._id !== id)
        evictedAtRef.current.set(id, Date.now())
    }

    // Socket: every task change on every project I am on, whether or not the project
    // is loaded. Removals and edits to listed tasks apply in place; a task that just
    // became mine needs its mission, so it comes through an incremental load.
    useEffect(() => {
        let rerankTimer
        let fetchTimer

        const rerankSoon = () => {
            clearTimeout(rerankTimer)
            rerankTimer = setTimeout(() => {
                workManagerRef.current(actionItemsRef.current, missionMapRef.current)
            }, 150)
        }

        const fetchSoon = () => {
            clearTimeout(fetchTimer)
            fetchTimer = setTimeout(() => {
                if (app.state.home && getTasksRanRef.current) getTasksRef.current()
            }, 600)
        }

        const off = onActionItemChanges((operation, changes) => {
            const myId = app.state.person?._id
            let changed = false
            let fetch = false

            changes.forEach((change) => {
                const doc = change?.doc
                const id = change?._id || doc?._id
                if (!id) return

                const cached = actionItemsRef.current.find((ai) => ai._id === id)

                if (operation === "delete") {
                    if (cached) {
                        evictTask(id)
                        changed = true
                    }
                    return
                }

                // A partial doc cannot say whether the task is still mine and open
                if (!doc || !Array.isArray(doc.people) || !doc.status) {
                    if (cached) fetch = true
                    return
                }

                const isMine = doc.people.some((p) => getRefId(p) === myId)
                const isLive = isMine && !doc._archived && (doc.status === "open" || doc.status === "blocked")

                if (cached) {
                    if (isLive) {
                        actionItemsRef.current = actionItemsRef.current.map((ai) =>
                            ai._id === id ? { ...ai, ...pickTaskFields(doc) } : ai,
                        )
                    } else {
                        evictTask(id)
                    }
                    changed = true
                } else if (isLive) {
                    fetch = true
                }
            })

            if (changed) rerankSoon()
            if (fetch) fetchSoon()
        })

        // Its tasks are archived with it, without a task message of their own
        const offMissionDeleted = onMissionDeleted((missionId) => {
            const gone = actionItemsRef.current.filter((ai) => ai.missionId === missionId)

            delete missionMapRef.current[missionId]

            if (!gone.length) return

            gone.forEach((ai) => evictTask(ai._id))
            rerankSoon()
        })

        // Sockets drop while the machine sleeps or the tab is in the background
        const onVisible = () => {
            if (document.visibilityState === "visible" && app.state.home && getTasksRanRef.current) {
                getTasksRef.current()
            }
        }
        document.addEventListener("visibilitychange", onVisible)

        return () => {
            off()
            offMissionDeleted()
            clearTimeout(rerankTimer)
            clearTimeout(fetchTimer)
            document.removeEventListener("visibilitychange", onVisible)
        }
    }, [])

    const workManager = (ais, miMap) => {
        let toDos = []
        let orgInvites = []
        let missionInvites = []

        const missionCount = missionCountRef.current

        //Notifications
        orgInvites = notificationList
            .filter((n) => n.isDisabled !== true && n.type === "orgInvite")
            .map((n) => {
                return {
                    isOrgInvite: n.type === "orgInvite",
                    data: n,
                }
            })

        missionInvites = listableMissionInvites(notificationList, gtSsoTotal).map((n) => {
            return {
                isMissionInvite: n.type === "invite",
                data: n,
            }
        })

        // GT SSO people are active members already: past GT_SSO_INVITES_BUNDLE_AT, one awareness card
        let gtSsoBundle
        const gtSso = gtSsoInviteCount(notificationList, gtSsoTotal)

        if (gtSso.bundled) {
            gtSsoBundle = {
                isMissionInvite: true,
                isGtSsoBundle: true,
                count: gtSso.count,
                _id: "gt-sso-invites",
                data: { _id: "gt-sso-invites", type: "invite", isDisabled: false, isRead: false },
            }
        }

        if (missionInvites.length > 3) {
            const allInvitesToOne = missionInvites.slice()

            missionInvites = [
                {
                    isMissionInvite: true,
                    isAllInOne: true,
                    allInvites: allInvitesToOne,
                    _id: "all-in-one-invite",
                    data: {
                        body: "Welcome to the mission!",
                        isDisabled: false,
                        isRead: false,
                        mission: { title: "Empty" },
                        onModel: "MissionInvite",
                        title: "Welcome to the Mission",
                        type: "invite",
                        _id: "all-in-one-invite",
                        ref: app.state.person,
                    },
                },
            ]
        }

        if (gtSsoBundle) missionInvites.unshift(gtSsoBundle)

        //Helpers
        if (app.state.person && !app.state.person.emailVerified) {
            toDos.push({ ...helperVerifyEmail, people: [app.state.person] })
        }

        if (app.state.orgs.length === 0 && app.state.orgsLoaded) {
            toDos.push({ ...helperStartNewOrg, people: [app.state.person] })
        }

        // A new sign up can look around a full organization before setting up their own. Always the
        // last tile shown, so their own work comes first once they have some
        const demoTourTile =
            (app.state.orgs.length === 0 || isDemoTourTester(app.state.person)) &&
            !isDemoTourOn() &&
            demoTourStatus?.available &&
            (demoTourStatus.eligible || demoTourStatus.tour?.active)
                ? { ...tileHelperDemoTour(() => setShowDemoTour(true)), people: [app.state.person] }
                : null

        // The count is fetched once on mount; tasks that arrived since prove there are missions
        if (!missionCount && !ais.length && app.state.orgs.length) {
            toDos.push({ ...helperStartNewMission, people: [app.state.person] })
        } else {
            // This tab's own edits land in app.state.missions first; the cache only
            // catches up on the next full load
            const loaded = makeHash(app.state.missions || [])
            const missionOf = (id) => {
                const cached = missionMapRef.current[id]
                const live = loaded[id]

                if (!cached || !live) return cached

                const fresh = {}
                TILE_MISSION_FIELDS.forEach((f) => {
                    if (live[f] !== undefined) fresh[f] = live[f]
                })

                return { ...cached, ...fresh }
            }

            const recs = recommender(Object.values(miMap), app, ais, new Date()).map((ai) => {
                const mission = missionOf(ai.missionId)

                return {
                    _id: ai._id,
                    actionItem: ai,
                    missionData: mission,
                    orgData: app.state.orgs.find((o) => getObjectId(o) === getObjectId(mission.org)),
                    app: app,
                    onClick: () => {
                        app.showActionItemDetails(ai)
                    },
                }
            })

            toDos.push(...recs)
        }

        // Single org and nothing to act on (e.g. landing here right after verifying): point at the org
        if (
            app.state.orgs.length === 1 &&
            canAccessOrgSpace(app.state.person, app.state.orgs[0]) &&
            !toDos.some((t) => !t?._id?.includes("helper"))
        ) {
            toDos.unshift({ ...helperVisitBase(getObjectId(app.state.orgs[0])), people: [app.state.person] })
        }

        // Tiles the async checks put in (leaves, sign-offs, vendor invoices) are not rebuilt
        // here; dropping them would wipe them on every task load until their check re-ran
        const checkTiles = (recommendationsRef.current || []).filter((r) => CHECK_TILE_IDS.includes(r?._id))

        let finalRecommendations = [...checkTiles, ...orgInvites, ...missionInvites, ...toDos]

        if (finalRecommendations.filter((h) => !h?._id?.includes("helper")).length === 0 && missionCount > 0) {
            finalRecommendations.push({ ...helperNothingPlanned, people: [app.state.person] })
        }

        const sl = window.innerWidth < 1300 ? 4 : 5

        if (demoTourTile) {
            setRecommendations([...finalRecommendations.slice(0, sl - 1), demoTourTile])
        } else {
            setRecommendations(finalRecommendations.slice(0, sl))
        }
    }

    // "Remove tour tile" on the welcome screen: the tour ends for good and the tile goes.
    // The welcome closes first, so the confirm is not hidden behind it.
    const confirmRemoveTourTile = () => {
        setShowDemoTour(false)
        app.confirm({
            comp: <p>Remove the tour action item? You won't be able to open the demo tour again.</p>,
            yesText: "Remove tour tile",
            onYes: () => {
                endDemoTour()
                    .then((res) =>
                        setDemoTourStatus((s) => ({ ...(s || {}), eligible: false, tour: res?.tour || null })),
                    )
                    .catch(() => toast.error("Sorry, that didn't work. Please try again."))
            },
        })
    }

    const workManagerRef = useRef()
    workManagerRef.current = workManager

    const leaveChecks = async () => {
        const pmLeaveCount = await getLeavesBadge({ app })

        if (pmLeaveCount) {
            if (!recommendationsRef.current?.find((l) => l._id === "helper-leaves")) {
                let newRecommendations = recommendationsRef.current?.slice()

                newRecommendations.unshift(
                    tileHelperLeaves(
                        () => {
                            history.push("/board?board-tab=leaves")
                        },
                        [app.state.person],
                        pmLeaveCount || 0,
                    ),
                )

                setRecommendations(newRecommendations)
            }
        } else {
            let newRecommendations2 = recommendationsRef.current.slice()
            let helperIndex = newRecommendations2.findIndex((l) => l._id === "helper-leaves")

            if (helperIndex !== -1) {
                newRecommendations2.splice(helperIndex, 1)
                setRecommendations(newRecommendations2)
            }
        }
    }

    // Asks the invoices themselves rather than counting unread notifications: a
    // notification's read state has nothing to do with whether anything was approved, so
    // the old notification-based tile vanished the moment it was clicked, approved or
    // not. `countOnly` keeps this to a count query - the list is only fetched once the
    // card is actually opened.
    // The "sign-offs waiting for you" tile, like the vendor invoice one.
    const signOffChecks = async (knownCount) => {
        let count = knownCount

        if (count === undefined) {
            try {
                count = await countWaitingSignOffs(app.state.orgs || [])
            } catch (e) {
                return
            }
        }

        const helperIndex = (recommendationsRef.current || []).findIndex((l) => l._id === "helper-sign-offs")

        if (count) {
            let newRecommendations = recommendationsRef.current?.slice() || []
            const tile = tileHelperSignOffs(() => setShowSignOffs(true), [app.state.person], count)

            if (helperIndex !== -1) {
                newRecommendations[helperIndex] = tile
            } else {
                newRecommendations.unshift(tile)
            }

            setRecommendations(newRecommendations)
        } else if (helperIndex !== -1) {
            const newRecommendations2 = recommendationsRef.current.slice()
            newRecommendations2.splice(helperIndex, 1)
            setRecommendations(newRecommendations2)
        }
    }

    const invoiceApprovalChecks = async (knownCount) => {
        let count = knownCount

        if (count === undefined) {
            try {
                const resp = await getMyVendorInvoiceApprovals({ countOnly: true })
                count = resp?.count || 0
            } catch (e) {
                return
            }
        }

        const helperIndex = (recommendationsRef.current || []).findIndex((l) => l._id === "helper-vendor-invoices")

        if (count) {
            // Rebuilt rather than skipped when it's already there, so the count on the
            // tile keeps up as invoices are approved from the card.
            let newRecommendations = recommendationsRef.current?.slice() || []

            const tile = tileHelperVendorInvoices(
                () => {
                    setShowInvoiceApprovals(true)
                },
                [app.state.person],
                count,
            )

            if (helperIndex !== -1) {
                newRecommendations[helperIndex] = tile
            } else {
                newRecommendations.unshift(tile)
            }

            setRecommendations(newRecommendations)
        } else if (helperIndex !== -1) {
            let newRecommendations2 = recommendationsRef.current.slice()
            newRecommendations2.splice(helperIndex, 1)
            setRecommendations(newRecommendations2)
        }
    }

    const cn = cx("dna-home", myTheme || "empty", {
        "fully-loaded": !loading,
        "task-status-open": context === "taskStatus" && !isGtUser(app),
    })

    const isDark = darkThemes.includes(myTheme)

    return (
        <>
            {myTheme === "christmas-theme" && !app.state.board && <SnowFall />}
            <DemoTourWelcome
                open={showDemoTour}
                onClose={() => setShowDemoTour(false)}
                onRemoveTile={confirmRemoveTourTile}
                days={demoTourStatus?.tour?.active ? daysUntil(demoTourStatus.tour.expiresAt) : DEMO_TOUR_DAYS}
            />
            <div className={cn}>
                <HomeMenu setContext={setContext} context={context} app={app} isDark={isDark} />

                <div
                    className={cx("x-home-loading-bar", {
                        "dna-dark": isDark,
                    })}
                >
                    <BarLoader color={"#666666"} loading={true} width={80} />
                </div>

                {!loading && (
                    <>
                        {context === "workAI" && (
                            <>
                                <SimpleBarReact className={cx("x-home-scroll", {})}>
                                    <AnimatePresence>
                                        <motion.ul className="x-work-ai" layout style={{ width: 220, height: 400 }}>
                                            {recommendations.length === 0 && (
                                                <div className="nothing-to-do-here">
                                                    <h1>🏝️</h1>
                                                    <p>Not much going on for actvities</p>
                                                </div>
                                            )}
                                            {recommendations.slice(0, 6).map((r, i) => {
                                                return (
                                                    <TileComp
                                                        key={r._id}
                                                        recommendation={r}
                                                        index={i}
                                                        me={app.state.person}
                                                        setShowMissionInvite={setShowMissionInvite}
                                                        setShowOrgInvite={setShowOrgInvite}
                                                        setShowAllMissinInvites={setShowAllMissinInvites}
                                                        getTasks={getTasks}
                                                    />
                                                )
                                            })}
                                        </motion.ul>
                                    </AnimatePresence>
                                </SimpleBarReact>
                                <TeamActivity app={app} isDark={isDark} />
                            </>
                        )}

                        <AnimatePresence>
                            {context === "taskStatus" && !isGtUser(app) && (
                                <motion.div
                                    key="task-status"
                                    className={cx("x-task-status-tab", { "dna-dark": isDark })}
                                    transition={{ duration: 0.4, ease: "easeInOut" }}
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0 }}
                                >
                                    <ErrorBoundary app={app} context="task-status">
                                        <TaskStatus app={app} />
                                    </ErrorBoundary>
                                </motion.div>
                            )}
                        </AnimatePresence>

                        <AnimatePresence>
                            {context === "taskMaster" && (
                                <motion.div
                                    className={isDark ? "dna-dark" : undefined}
                                    transition={{ duration: 2, type: "spring", bounce: 0.5 }}
                                    initial={{ opacity: 0, zIndex: 0, transform: "translateY(160px)" }}
                                    animate={{ opacity: 1, zIndex: 0, transform: "translateY(80px)" }}
                                    exit={{ opacity: 0, zIndex: 0, transform: "translateY(160px)" }}
                                >
                                    <ErrorBoundary app={app} context="task-master">
                                        <TaskMaster
                                            app={app}
                                            stateChange={missionChange}
                                            recommendations={recommendations}
                                            setContext={setContext}
                                            fields={fields}
                                            taskMasterRef={taskMasterRef}
                                        />
                                    </ErrorBoundary>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </>
                )}

                <Bubble
                    app={app}
                    tag="x-home-tour-nav"
                    x={50}
                    y={70}
                    top={0}
                    delay={0}
                    position="fixed"
                    arrow={"top"}
                    msg={
                        <>
                            <p>
                                <b>Control missionX</b> <PiNavigationArrowDuotone color="rgb(0, 255, 0)" />
                            </p>
                            <p>
                                <p>Navigate anywhere, create things, view your projects and check notifications.</p>
                            </p>
                        </>
                    }
                />
                {app.state.orgs?.length > 0 && (
                    <Bubble
                        app={app}
                        tag="x-home-tour-admin"
                        x={0}
                        y={0}
                        top={53}
                        right={90}
                        delay={0.3}
                        position="fixed"
                        arrow={"right"}
                        msg={
                            <>
                                <p>
                                    Your admin tools <PiIceCreamDuotone color="rgb(0, 255, 0)" />
                                </p>
                                <p>Time tracking, leave requests, expenses, profile settings, and calendar.</p>
                            </>
                        }
                    />
                )}
                {app.hasSeenMsg("x-home-tour-admin") && Object.keys(missionMapRef.current).length > 0 && (
                    <Bubble
                        app={app}
                        tag="x-home-tour-tasks"
                        x={-30}
                        y={60}
                        top={0}
                        left={"50%"}
                        delay={0.3}
                        position="fixed"
                        arrow={"top"}
                        msg={
                            <>
                                <p>
                                    Work management <AiTwotoneThunderbolt color="rgb(0, 255, 0)" />
                                </p>
                                <p>
                                    View your daily curated tasks or switch to Task master for more power and control in
                                    viewing task updates across projects and people.
                                </p>
                            </>
                        }
                    />
                )}

                {!app.state.board && !app.state.timeTracking && (
                    <>
                        {context !== "taskMaster" && (
                            <Eggs setTheme={setTheme} app={app} isDark={isDark || myTheme === "japan-theme"} />
                        )}
                    </>
                )}
            </div>

            <Card
                center
                portal
                modal
                className="x-all-invites"
                open={showAllMissinInvites}
                onClose={() => setShowAllMissinInvites(null)}
                style={{ maxWidth: 1000, width: 1200 }}
            >
                {showAllMissinInvites && (
                    <AcceptAllInvites
                        onClose={() => {
                            setShowAllMissinInvites(null)
                        }}
                        // Live, not the tile's snapshot: invites that land while it's open show up too.
                        notifications={listableMissionInvites(notificationList, gtSsoTotal).map((n) => ({
                            isMissionInvite: true,
                            data: n,
                        }))}
                        onAccepted={reloadTasksAfterAccept}
                        app={app}
                    />
                )}
            </Card>

            <Card
                center
                portal
                modal
                className="x-invoice-approvals"
                open={showInvoiceApprovals}
                onClose={() => setShowInvoiceApprovals(false)}
                style={{ maxWidth: 1100, width: 1300 }}
            >
                {showInvoiceApprovals && (
                    <VendorInvoiceApprovals
                        app={app}
                        onClose={() => setShowInvoiceApprovals(false)}
                        onCountChange={(count) => invoiceApprovalChecks(count)}
                    />
                )}
            </Card>

            <Card
                center
                portal
                modal
                className="x-sign-off-approvals"
                open={showSignOffs}
                onClose={() => setShowSignOffs(false)}
                style={{ maxWidth: 900, width: 900 }}
            >
                {showSignOffs && (
                    <SignOffInbox
                        app={app}
                        onClose={() => setShowSignOffs(false)}
                        onCountChange={(count) => signOffChecks(count)}
                    />
                )}
            </Card>

            <Card
                closeButton
                onClose={() => {
                    setShowMissionInvite(false)
                    setShowOrgInvite(false)
                }}
                style={{ width: 500 }}
                className="dna-dark dna-dark-bg"
                open={Boolean(showMissionInvite) || Boolean(showOrgInvite)}
                center
                portal
                modal
            >
                <SimpleBarReact style={{ maxHeight: "95vh" }}>
                    {showMissionInvite && (
                        <WelcomeToMission
                            invite={showMissionInvite.data}
                            app={app}
                            onExit={() => {
                                setShowMissionInvite(false)
                            }}
                            onAccepted={reloadTasksAfterAccept}
                        />
                    )}
                    {showOrgInvite && (
                        <WelcomeToOrg
                            invite={showOrgInvite.data}
                            app={app}
                            onExit={(orgData) => {
                                setShowOrgInvite(false)
                            }}
                        />
                    )}
                </SimpleBarReact>
            </Card>
        </>
    )
}

const Eggs = ({ setTheme, app, isDark }) => {
    return (
        <>
            <ul className={isDark ? "egg-shells dna-dark" : "egg-shells"}>
                {/*
                <li
                    onClick={(e) => {
                        app.xBurst(e.pageX, e.pageY)
                        setTheme("christmas-theme")
                    }}
                >
                    <GiPineTree color="#4aff89" fontSize={28} style={{ transform: "translate(-8px, -8px)" }} />
                </li>
                */}
                <li
                    onClick={(e) => {
                        app.circleBurst(e.pageX, e.pageY)
                        setTheme(null)
                    }}
                >
                    <GiCosmicEgg />
                </li>
                {app.state.person.emailList.find((e) => e.email.includes("catchengineering") && e.verified) && (
                    <li
                        onClick={(e) => {
                            app.xBurst(e.pageX, e.pageY)
                            setTheme("catch-theme")
                        }}
                    >
                        <GiCosmicEgg />
                    </li>
                )}
                {app.state.person.emailList.find((e) => e.email.includes("solarcenturyafrica.com") && e.verified) && (
                    <li
                        onClick={(e) => {
                            app.xBurst(e.pageX, e.pageY)
                            setTheme("sca-theme")
                        }}
                    >
                        <GiCosmicEgg />
                    </li>
                )}
                <li
                    onClick={(e) => {
                        app.xBurst(e.pageX, e.pageY)
                        setTheme("peak-theme")
                    }}
                >
                    <GiCosmicEgg />
                </li>
                <li
                    onClick={(e) => {
                        app.xBurst(e.pageX, e.pageY)
                        setTheme("blue-sky-theme")
                    }}
                >
                    <GiCosmicEgg />
                </li>

                <li
                    onClick={(e) => {
                        app.xBurst(e.pageX, e.pageY)
                        setTheme("japan-theme")
                    }}
                >
                    <GiCosmicEgg />
                </li>
                <li
                    onClick={(e) => {
                        app.xBurst(e.pageX, e.pageY)
                        setTheme("banksy-theme-1")
                    }}
                >
                    <GiCosmicEgg />
                </li>
                <li
                    onClick={(e) => {
                        app.xBurst(e.pageX, e.pageY)
                        setTheme("stars-theme")
                    }}
                >
                    <GiCosmicEgg />
                </li>
                <li
                    onClick={(e) => {
                        app.xBurst(e.pageX, e.pageY)
                        setTheme("x-ev-theme")
                    }}
                >
                    <GiCosmicEgg />
                </li>
                <li
                    onClick={(e) => {
                        app.xBurst(e.pageX, e.pageY)
                        setTheme("banksy-theme")
                    }}
                >
                    <GiCosmicEgg />
                </li>

                <li
                    onClick={(e) => {
                        app.xBurst(e.pageX, e.pageY)
                        setTheme("main-theme")
                    }}
                >
                    <GiCosmicEgg />
                </li>

                <li
                    onClick={(e) => {
                        app.xBurst(e.pageX, e.pageY)
                        setTheme("retro-tech-theme")
                    }}
                >
                    <GiCosmicEgg />
                </li>
                <li
                    onClick={(e) => {
                        app.xBurst(e.pageX, e.pageY)
                        setTheme("pool-days-theme")
                    }}
                >
                    <GiCosmicEgg />
                </li>

                <Bubble
                    app={app}
                    tag="x-home-tour-eggs"
                    x={-60}
                    y={-195}
                    delay={0.4}
                    arrow={"down"}
                    msg={
                        <>
                            <p>
                                <b>Change your theme</b> <PiImageBrokenDuotone color="rgb(0, 255, 0)" />
                            </p>
                            <p>
                                <p>Personalize your background theme.</p>
                            </p>
                        </>
                    }
                />
            </ul>
        </>
    )
}

/** GT gets the bare minimum: no Task status (the API leaves their projects out too). */
const isGtUser = (app) => !!app.state.orgs?.some((o) => isGtGr(o))

/** The home views that are tabs. */
const HOME_TABS = ["workAI", "taskMaster", "taskStatus"]

/**
 * Remembers the view, so the next arrival on the home screen opens it.
 *
 * @param app {Object}
 * @param tab {String} One of HOME_TABS
 */
const saveHomeTab = (app, tab) => {
    const currentAppUiState = app?.state?.person?.appUiState ?? {}
    const currentHomeState = currentAppUiState.homeState ?? {}

    app.personUpdate({
        appUiState: {
            ...currentAppUiState,
            homeState: {
                ...currentHomeState,
                tab,
            },
        },
    })
}

const HomeMenu = ({ setContext, context, app, isDark }) => {
    return (
        <ul className={cx("x-home-nav", { "dna-dark": isDark })}>
            <li
                className={context === "workAI" ? "active" : undefined}
                onClick={(e) => {
                    setContext("workAI")
                    saveHomeTab(app, "workAI")
                    app.xBurst(e.pageX, e.pageY)
                }}
            >
                <PiTriangleDuotone /> Work AI
            </li>

            <li
                className={context === "taskMaster" ? "active" : undefined}
                onClick={(e) => {
                    setContext("taskMaster")
                    saveHomeTab(app, "taskMaster")
                    app.xBurst(e.pageX, e.pageY)
                }}
            >
                <PiHexagonDuotone /> Task master
            </li>
            {!isGtUser(app) && (
                <li
                    className={context === "taskStatus" ? "active" : undefined}
                    onClick={(e) => {
                        setContext("taskStatus")
                        saveHomeTab(app, "taskStatus")
                        app.xBurst(e.pageX, e.pageY)
                    }}
                >
                    <PiCircleDuotone /> Task status
                </li>
            )}
        </ul>
    )
}

const TileComp = ({
    recommendation,
    index,
    setShowMissionInvite,
    setShowOrgInvite,
    me,
    setShowAllMissinInvites,
    getTasks,
}) => {
    //  const [isOpen, setIsOpen] = useState(false)

    let x = 0,
        scale = 1

    switch (index) {
        case 0:
            break
        case 1:
            x = 235
            scale = 0.9
            break
        case 2:
            x = -224
            scale = 0.8
            break
        case 3:
            x = 432
            scale = 0.7
            break
        case 4:
            x = -400
            scale = 0.6
            break
        case 5:
            x = 580
            scale = 0.5
            break

        default:
    }

    return (
        <motion.li
            initial={{
                opacity: 0,
                y: 170,
                x: x,
                scale: scale,
            }}
            transition={{ duration: 1.8, delay: 0.1 * index, type: "spring", bounce: 0.5 }}
            animate={{
                opacity: 1,
                y: 0,
                x: x,
                scale: scale,
            }}
            exit={{ opacity: 0, y: 170 }}
        >
            <RecommendationComp
                recommendation={recommendation}
                setShowMissionInvite={setShowMissionInvite}
                setShowOrgInvite={setShowOrgInvite}
                setShowAllMissinInvites={setShowAllMissinInvites}
                me={me}
                getTasks={getTasks}
            />
        </motion.li>
    )
}

const RecommendationComp = ({
    recommendation,
    setShowMissionInvite,
    setShowOrgInvite,
    me,
    setShowAllMissinInvites,
    getTasks,
}) => {
    if (recommendation.isGtSsoBundle) {
        const data = {
            _id: recommendation._id,
            title: `Invited to ${recommendation.count} missions`,
            onClick: () => {
                // Off the screen now; the server marks them accepted in one call
                const ids = notificationStore
                    .getState()
                    .list.filter((n) => n.isDisabled !== true && n.type === "invite" && isGtSsoInvite(n))
                    .map((n) => n._id)

                notificationStore.removeMany(ids)
                toast.success("Good luck commander!")

                apiMission.acknowledgeGtSsoInvites().catch(() => {
                    toast.error("Sorry, we couldn't clear your invites. Please try again.")
                    notificationStore.reload()
                })
            },
            missionData: {
                title: "Welcome to the mission",
                useAsIs: true,
                theme: {
                    backgroundColor: "#000000",
                    backgroundImage: "url(/themes/missions/bg-14.jpg)",
                    dark: true,
                },
            },
            showMissionTitle: true,
            people: [me],
            progress: true,
            actionItems: [{ status: "open" }],
            fullBg: true,
        }

        return <InviteTile key={data._id} {...data} />
    }

    if (recommendation.isMissionInvite && recommendation.isAllInOne) {
        const data = {
            _id: recommendation.data._id,
            title: "Pending Mission Invites ",
            onClick: () => {
                if (
                    recommendation?.data.ref.inviteeEmail &&
                    !isUserEmailVerified(me, recommendation?.data.ref.inviteeEmail)
                ) {
                    if (!new URLSearchParams(window.location.search).get("myspace")) {
                        toast.warning("You must verify your email before accepting any invites", { autoClose: 6000 })
                        addParam("myspace", "open")
                    }
                    return
                }

                setShowAllMissinInvites(recommendation)
            },
            missionData: { ...recommendation.data.mission, title: "Review invitations" },
            icon: "square",
            showMissionTitle: true,
            people: [me],
            progress: true,
            actionItems: recommendation.allInvites.map((d) => ({ status: "open" })),
        }

        return <ActionTile key={data._id} {...data} />
    } else if (recommendation.isMissionInvite) {
        const data = {
            _id: recommendation.data._id,
            title: "Welcome to the mission.",
            onClick: () => {
                if (
                    recommendation?.data.ref.inviteeEmail &&
                    !isUserEmailVerified(me, recommendation?.data.ref.inviteeEmail)
                ) {
                    if (!new URLSearchParams(window.location.search).get("myspace")) {
                        toast.warning("Please verify your email first.", { autoClose: 6000 })
                        addParam("myspace", "open")
                    }
                    return
                }

                setShowMissionInvite(recommendation)
            },
            missionData: { ...recommendation.data.mission, title: "Invite Pending", useAsIs: true },
            showMissionTitle: true,
            people: [me],
            progress: true,
            actionItems: [{ status: "open" }],
        }
        return <InviteTile key={data._id} {...data} />
    }
    if (recommendation.isOrgInvite) {
        const data = {
            _id: recommendation.data._id,
            title: "Welcome to the organization.",
            onClick: () => {
                if (
                    recommendation?.data.ref.inviteeEmail &&
                    !isUserEmailVerified(me, recommendation?.data.ref.inviteeEmail)
                ) {
                    if (!new URLSearchParams(window.location.search).get("myspace")) {
                        toast.warning("Please verify your email first.", { autoClose: 6000 })
                        addParam("myspace", "open")
                    }
                    return
                }

                setShowOrgInvite(recommendation)
            },
            missionData: {
                title: "Invite Pending",
                useAsIs: true,

                theme: {
                    backgroundColor: "#1a134d",
                    backgroundImage: "url(/themes/orgs/org-1-tile.jpg)",
                    dark: true,
                },
            },
            showMissionTitle: true,
            people: [me],
            progress: true,
            actionItems: [{ status: "open" }],
            fullBg: true,
        }
        return <InviteTile {...data} />
    }

    if (recommendation.from === "ai") {
        return <ActionTile {...recommendation} />
    }

    return <ActionItemTile actionItem={recommendation.actionItem} {...recommendation} />
}

const helperVerifyEmail = {
    _id: "verify-email-helper",
    title: "Verify your email",
    showMissionTitle: true,
    msg: "Please check your email for the magic link that unlocks missionX.",
    progress: true,
    from: "ai",
    icon: "fire",
    iconColor: "#ff3b30",
    missionData: { title: "Required" },
    actionItems: [{ status: "open" }],
    onClick: () => {
        addParam("myspace", "open")
    },
}

const helperStartNewOrg = {
    _id: "new-org-helper",
    title: "Establish your organization",
    showMissionTitle: true,
    msg: "Your base of operations",
    progress: true,
    from: "ai",
    icon: "triangle",
    missionData: { title: "STEP 1" },
    actionItems: [{ status: "open" }],
    onClick: () => {
        addParam("hq", "true")
    },
}

const helperVisitBase = (orgId) => ({
    _id: "visit-base-helper",
    title: "Visit your Organization",
    showMissionTitle: true,
    progress: true,
    msg: "Check on your organization",
    from: "ai",
    icon: "triangle",
    missionData: { title: "Base of operations" },
    actionItems: [{ status: "open" }],
    onClick: () => {
        addParam("org", orgId)
    },
})

const helperStartNewMission = {
    _id: "new-mission-helper",
    title: "Create a project",
    showMissionTitle: true,
    msg: "Plan, budget and manage work to be done.",
    progress: true,
    actionItems: [{ status: "open" }],
    from: "ai",
    icon: "gantt",
    missionData: { title: "Quick start" },
    onClick: () => {
        addParam("create-mission", "task")
    },
}

const helperLearnAbout = {
    _id: "vide-guides",
    title: "Systems manual",
    msg: "Unlock powerful insights",
    showMissionTitle: true,
    from: "ai",
    icon: "workspaces",
    missionData: { title: "Getting started" },
    progress: true,
    actionItems: [{ status: "open" }],
    onClick: () => {
        window.open("https://www.missionx.ai/guide", "_blank").focus()
    },
}

const DEMO_TOUR_DAYS = 7

const daysUntil = (date) => Math.max(1, Math.ceil((new Date(date).getTime() - Date.now()) / (24 * 60 * 60 * 1000)))

const tileHelperDemoTour = (onClick) => ({
    _id: "helper-demo-tour",
    title: "Tour a live organization",
    msg: "Look around a fully set up organization",
    showMissionTitle: true,
    progress: true,
    from: "ai",
    icon: "workspaces",
    missionData: { title: "Demo" },
    actionItems: [{ status: "open" }],
    onClick: onClick,
})

const tileHelperLeaves = (onClick, people, amount = 0) => {
    return {
        _id: "helper-leaves",
        title: `Leave requests ready for review`,
        msg: "Review the leave requests affecting your projects",
        showMissionTitle: true,
        progress: true,
        from: "ai",
        icon: "leave",
        people: people,
        isAdminTask: true,
        missionData: { title: "For the PM" },
        onClick: onClick,
        actionItems: Array(amount)
            .fill(null)
            .map(() => ({ title: "A" })),
    }
}

const tileHelperSignOffs = (onClick, people, amount = 0) => {
    return {
        _id: "helper-sign-offs",
        title: `${amount > 10 ? "10+" : amount} sign-off${amount === 1 ? "" : "s"} waiting for you`,
        msg: "Review what you were asked to sign off, and approve or reject it",
        showMissionTitle: true,
        progress: true,
        from: "ai",
        icon: "fire",
        people: people,
        isAdminTask: true,
        missionData: { title: "Sign-off needed" },
        actionItems: [],
        onClick: onClick,
    }
}

const tileHelperVendorInvoices = (onClick, people, amount = 0) => {
    return {
        _id: "helper-vendor-invoices",
        title: `${amount > 10 ? "10+" : amount} vendor invoice${amount === 1 ? "" : "s"} awaiting your approval`,
        msg: "Review and sign off the invoices waiting on you",
        showMissionTitle: true,
        progress: true,
        from: "ai",
        icon: "expenses",
        people: people,
        isAdminTask: true,
        missionData: { title: "Approval needed" },
        actionItems: [],
        onClick: onClick,
    }
}

const helperNothingPlanned = {
    _id: "check-on-plans",
    title: "Check on your projects?",
    msg: "No tasks planned for today.",
    showMissionTitle: true,
    progress: true,
    from: "ai",
    icon: "barChart",
    missionData: { title: "Idea" },
    actionItems: [{ status: "open" }],
    onClick: () => {
        history.push("/board")
    },
}

export default Home
